import type { ComponentIR } from "../ir.js";
import type { GeneratedFile, GenerateOptions, Generator } from "./types.js";
import { operationTypeNames, toPascalCase } from "./schema-to-ts.js";

function handlerImportPath(functionName: string): string {
  // Convention: handler file lives at ../handlers/{functionName}.js
  // (relative from generated/component.runtime.ts)
  return `../handlers/${functionName}.js`;
}

function serializeComponent(ir: ComponentIR): string {
  const info: Record<string, string> = {
    id: ir.info.id,
    version: ir.info.version,
  };
  if (ir.info.description) {
    info.description = ir.info.description;
  }

  return JSON.stringify(
    {
      schema: ir.schema,
      info,
      projections: ir.projections,
      operations: ir.operations,
    },
    null,
    2,
  );
}

function buildHandlerImportsAndMap(
  ir: ComponentIR,
): { imports: string[]; handlersEntries: string[] } {
  const imports: string[] = [];
  const handlersEntries: string[] = [];

  for (const [operationName, op] of Object.entries(ir.operations)) {
    if (!op.handler) {
      continue;
    }

    const functionName = op.handler;
    const importPath = handlerImportPath(functionName);
    const alias = `_handler_${functionName}`;
    imports.push(
      `import { ${functionName} as ${alias} } from ${JSON.stringify(importPath)};`,
    );
    const names = operationTypeNames(operationName);
    handlersEntries.push(
      `  ${JSON.stringify(operationName)}: async (_options, input, _ctx) => ${alias}(input as ${names.input}),`,
    );
  }

  return { imports, handlersEntries };
}

function countAgenticOperations(ir: ComponentIR): number {
  return Object.values(ir.operations).filter((op) => !op.handler).length;
}

export const runtimeGenerator: Generator = {
  generate(ir: ComponentIR, options: GenerateOptions): GeneratedFile[] {
    const componentConst = `${toPascalCase(ir.info.id)}Component`;
    const { imports: handlerImports, handlersEntries } =
      buildHandlerImportsAndMap(ir);

    const hasHandlers = handlersEntries.length > 0;
    const bindingCall = hasHandlers
      ? `createRuntimeBinding(${componentConst} as any, {\n${handlersEntries.join("\n")}\n})`
      : `createRuntimeBinding(${componentConst} as any)`;

    const agenticOpsCount = countAgenticOperations(ir);
    const hasPromptRouting = agenticOpsCount >= 2;

    const typeImports =
      Object.keys(ir.operations).length > 0
        ? `import type {
${Object.keys(ir.operations)
  .flatMap((name) => {
    const n = operationTypeNames(name);
    return [`  ${n.input},`, `  ${n.output},`, `  ${n.options},`];
  })
  .join("\n")}
} from "./component.types.js";
`
        : `import type {} from "./component.types.js";
`;

    const runtimeImports = hasPromptRouting
      ? `import {
  createRuntimeBinding,
  executePrompt as _executePrompt,
  type AaacInvocationContext,
} from "@aaac/runtime";`
      : `import {
  createRuntimeBinding,
  type AaacInvocationContext,
} from "@aaac/runtime";`;

    const promptExports = hasPromptRouting
      ? `
export const componentIR = ${componentConst} as any;
export { _binding as binding };
`
      : "";

    const executePromptMethod = hasPromptRouting
      ? `
  executePrompt(
    message: string,
    options: Record<string, unknown>,
    ctx: AaacInvocationContext,
  ): Promise<unknown> {
    return _executePrompt(message, ${componentConst} as any, _binding, ctx, options as any);
  },`
      : "";

    const content = `/** Auto-generated by @aaac/contracts. Do not edit. */
/* eslint-disable */

${runtimeImports}
${typeImports}${handlerImports.length > 0 ? `${handlerImports.join("\n")}\n` : ""}
const ${componentConst} = ${serializeComponent(ir)};

function applyMemoryRef(
  ctx: AaacInvocationContext,
  memoryRef?: string,
): AaacInvocationContext {
  if (!memoryRef) {
    return ctx;
  }
  return {
    ...ctx,
    memoryRef: {
      id: memoryRef,
      provider: "aaac",
      compat: "v1",
      created_at: new Date().toISOString(),
    },
  };
}

const _binding = ${bindingCall};
${promptExports}
export { type AaacInvocationContext };

export const runtime = {
  getOperationNames(): string[] {
    return _binding.getOperationNames();
  },
  getOperationIR(name: string) {
    return _binding.getOperationIR(name);
  },
  execute(
    operationName: string,
    options: Record<string, unknown>,
    input: unknown,
    ctx: AaacInvocationContext,
    memoryRef?: string,
  ): Promise<unknown> {
    return _binding.execute(
      operationName,
      options,
      input,
      applyMemoryRef(ctx, memoryRef),
    );
  },${executePromptMethod}
};
`;

    return [
      {
        path: "component.runtime.ts",
        content,
        overwrite: true,
      },
    ];
  },
};
